import {
  Add,
  CalendarMonth,
  CategoryOutlined,
  ContentCopy,
  ListAlt,
  Share,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveDateFotoUpload from 'components/SaveDateFotoUpload';
import React, { useState } from 'react';
import { ListaTemplateResumo } from 'types';
import { trackClick, usePageView } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import { formatarData, formatarDataHora } from 'utils/formatters';
import { copyLinkToClipboard, shareOrCopyLink } from 'utils/share';
import { getPublicBaseUrl, getPublicListUrl, getShareListUrl } from 'utils/shareUrl';
import { trackEvent } from 'utils/tracking';
import { useAdminListasRules } from './AdminListasPage.rules';

const AdminListasPage: React.FC = () => {
  usePageView('Minhas Listas');

  const {
    isGuestUser,
    isMasterAdmin,
    listas,
    templates,
    carregandoListas,
    isLoadingTemplates,
    error,
    successMessage,
    createDialogOpen,
    creating,
    createForm,
    createPixKeyInfo,
    editForm,
    slugPrivadoPreview,
    privateSlugPreview,
    templateSelecionado,
    pixHistory,
    loadingPixHistory,
    pixInput,
    pixKeyInfo,
    pixFeedback,
    selectedList,
    savingList,
    changingTemplate,
    confirmacoesPresenca,
    loadingConfirmacoesPresenca,
    rsvpDialogOpen,
    saveDateDialogOpen,
    rsvpForm,
    saveDateForm,
    abrirDialogCriar,
    fecharDialogCriar,
    handleTituloChange,
    handleTituloBlur,
    handlePixKeyChange,
    handlePixKeyBlur,
    handleTemplateChange,
    criarLista,
    abrirDialogEditar,
    salvarAlteracoes,
    handlePixInputChange,
    handlePixInputBlur,
    handleEditTituloChange,
    abrirGerenciarPresentes,
    abrirDialogPresenca,
    fecharDialogPresenca,
    abrirDialogSaveTheDate,
    fecharDialogSaveTheDate,
    handleRsvpToggle,
    handleRsvpDateChange,
    handleSaveDateToggle,
    handleSaveDateChange,
    salvarConfiguracaoPresenca,
    salvarConfiguracaoSaveTheDate,
    tipoListaInput,
    handleTipoListaChange,
    MAX_LISTAS,
  } = useAdminListasRules();

  const orderedTemplates = React.useMemo(() => {
    return [...templates].sort((a, b) => {
      if (a.slug === 'cha-de-bebe') return -1;
      if (b.slug === 'cha-de-bebe') return 1;
      return a.titulo.localeCompare(b.titulo, 'pt-BR');
    });
  }, [templates]);
  const showTypeSelector = !['chadebebe', 'listacasamento'].includes(BUILD_PROFILE.brand);
  const brandTheme = BUILD_PROFILE.theme;

  const mostrarSeletorTemplate = !BUILD_PROFILE.hideTemplateSelector;

  const [copiado, setCopiado] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<{ listId: string; message: string } | null>(
    null
  );
  const [showFloatingCreateButton, setShowFloatingCreateButton] = useState(false);
  const [showPixHistoryDialog, setShowPixHistoryDialog] = useState(false);
  const publicBaseUrl = getPublicBaseUrl();

  React.useEffect(() => {
    const updateVisibility = () => {
      const scrollTop = window.scrollY || window.pageYOffset;
      const viewportBottom = scrollTop + window.innerHeight;
      const distanceToBottom = document.documentElement.scrollHeight - viewportBottom;
      const shouldShow = scrollTop > 180 && distanceToBottom <= 360;
      setShowFloatingCreateButton(shouldShow);
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);

    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, []);

  const showShareFeedback = (listId: string, message: string) => {
    setShareFeedback({ listId, message });
    window.setTimeout(() => {
      setShareFeedback((current) => (current?.listId === listId ? null : current));
    }, 1800);
  };

  const handleShareLink = async (link: string, listId?: string, listTitle?: string) => {
    const normalizedTitle = (listTitle || '').trim();
    const shareTitle = normalizedTitle
      ? `Veja minha lista: ${normalizedTitle}`
      : 'Veja minha lista de presentes';
    const shareText = normalizedTitle
      ? `Fiz minha lista "${normalizedTitle}". Vem ver!`
      : 'Fiz minha lista de presentes. Vem ver!';

    try {
      const outcome = await shareOrCopyLink({
        url: link,
        title: shareTitle,
        text: shareText,
      });

      if (outcome === 'cancelled') {
        return;
      }

      trackEvent(outcome === 'shared' ? 'share_list_link_native' : 'copy_list_link', {
        source: 'admin_listas',
        listId,
      });
      if (listId) {
        showShareFeedback(
          listId,
          outcome === 'shared' ? 'Menu de compartilhamento aberto.' : 'Link copiado.'
        );
      } else {
        setCopiado(true);
        window.setTimeout(() => setCopiado(false), 1500);
      }
    } catch (err) {
      trackEvent('copy_list_link_error', {
        source: 'admin_listas',
        listId,
      });
      setCopiado(false);
    }
  };

  const handleCopyLink = async (link: string, listId: string) => {
    try {
      await copyLinkToClipboard(link);
      trackEvent('copy_list_link', {
        source: 'admin_listas',
        listId,
      });
      showShareFeedback(listId, 'Link copiado.');
    } catch (err) {
      trackEvent('copy_list_link_error', {
        source: 'admin_listas',
        listId,
      });
    }
  };

  const listasParaExibir = isMasterAdmin ? listas : listas.slice(0, 1);
  const podeCriarNovaLista = isMasterAdmin || listas.length < MAX_LISTAS;
  const hasEditChanges =
    !!selectedList &&
    (editForm.tituloLista.trim() !== selectedList.tituloLista.trim() ||
      pixInput.trim() !== (selectedList.pixKeyAtual?.chave ?? '').trim());

  if (carregandoListas) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {isGuestUser && (
        <Alert
          severity="info"
          sx={{ mt: -3, mb: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.70rem' } }}
          icon={false}
        >
          Você está como convidado — lista salva. Para não perdê-la, toque no ícone do perfil e
          vincule um e-mail.
        </Alert>
      )}

      {error && !createDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {listas.length === 0 ? (
        <Stack spacing={2}>
          <Alert severity="info">Você ainda não criou nenhuma lista.</Alert>
          {podeCriarNovaLista && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                trackClick('create_first_list_button');
                abrirDialogCriar();
              }}
            >
              Criar minha lista
            </Button>
          )}
        </Stack>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(auto-fit, minmax(320px, 1fr))' }}
          gap={3}
          alignItems="stretch"
        >
          {listasParaExibir.map((lista) => {
            const dataFormatada = formatarData(lista.dataCasamento);
            const encerrada = lista.ativa === false || !!lista.encerradaEm;
            const configuracoes = lista.configuracoesDetalhes ?? {};
            const rsvpAtivo = Boolean(configuracoes.enableRsvp);
            const saveDateAtivo = Boolean(configuracoes.enableSaveTheDate);
            const listUrl = getPublicListUrl(lista.slug);
            const shareUrl = getShareListUrl(lista.slug);
            const isEditingCurrentList = selectedList?.id === lista.id;

            return (
              <Card
                key={lista.id}
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: `1px solid ${brandTheme.palette.borderSoft}`,
                  background:
                    'linear-gradient(170deg, rgba(255,255,255,0.98) 0%, rgba(255,252,247,0.95) 100%)',
                  transition: 'transform 180ms ease, box-shadow 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 22px 34px rgba(39, 27, 15, 0.12)',
                  },
                }}
                onClick={() => {
                  if (!isEditingCurrentList) {
                    void abrirDialogEditar(lista);
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, px: 1 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Título da lista"
                        value={isEditingCurrentList ? editForm.tituloLista : lista.tituloLista}
                        onChange={(event) => handleEditTituloChange(event.target.value)}
                        disabled={savingList || changingTemplate || !isEditingCurrentList}
                        fullWidth
                        onClick={(event) => event.stopPropagation()}
                      />
                      {encerrada && <Chip label="Encerrada" size="small" color="error" />}
                    </Stack>

                    {showTypeSelector ? (
                      <FormControl
                        fullWidth
                        disabled={savingList || changingTemplate || !isEditingCurrentList}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <InputLabel id={`tipo-lista-card-label-${lista.id}`}>
                          Tipo de lista
                        </InputLabel>
                        <Select
                          labelId={`tipo-lista-card-label-${lista.id}`}
                          value={
                            isEditingCurrentList
                              ? tipoListaInput
                              : lista.tipoLista || lista.template?.slug || ''
                          }
                          onChange={(event) => handleTipoListaChange(event.target.value as string)}
                          label="Tipo de lista"
                        >
                          {orderedTemplates.map((t) => (
                            <MenuItem key={t.slug} value={t.slug}>
                              {t.titulo}
                            </MenuItem>
                          ))}
                        </Select>
                        {isEditingCurrentList && changingTemplate && (
                          <FormHelperText>
                            Atualizando modelo da lista. Isso pode levar alguns segundos...
                          </FormHelperText>
                        )}
                      </FormControl>
                    ) : null}

                    <Box>
                      <TextField
                        sx={{ mb: 1 }}
                        label="Chave PIX para receber"
                        value={isEditingCurrentList ? pixInput : (lista.pixKeyAtual?.chave ?? '')}
                        onChange={(event) => handlePixInputChange(event.target.value)}
                        onBlur={handlePixInputBlur}
                        placeholder="E-mail, telefone, CPF/CNPJ ou chave aleatória"
                        disabled={savingList || changingTemplate || !isEditingCurrentList}
                        error={
                          isEditingCurrentList &&
                          (pixFeedback?.severity === 'error' ||
                            (pixKeyInfo?.type === 'Invalida' && !pixKeyInfo.isValid))
                        }
                        onClick={(event) => event.stopPropagation()}
                        fullWidth
                        slotProps={{
                          input: {
                            endAdornment: isEditingCurrentList &&
                              pixKeyInfo &&
                              pixKeyInfo.type !== 'Vazia' && (
                                <Stack direction="row" marginBottom={1} alignItems="center">
                                  <Chip
                                    size="small"
                                    color={pixKeyInfo.isValid ? 'success' : 'error'}
                                    label={
                                      pixKeyInfo.type === 'Aleatoria'
                                        ? 'Chave aleatoria'
                                        : `Chave ${pixKeyInfo.type.toLowerCase()}`
                                    }
                                  />
                                </Stack>
                              ),
                          },
                        }}
                      />
                    </Box>

                    {dataFormatada && (
                      <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                        <CalendarMonth fontSize="small" />
                        <Typography variant="body2">
                          Data do evento: <strong>{dataFormatada}</strong>
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {isEditingCurrentList && hasEditChanges && (
                    <Button
                      variant="contained"
                      onClick={salvarAlteracoes}
                      disabled={savingList || changingTemplate}
                      fullWidth
                    >
                      {savingList ? 'Salvando...' : 'Salvar alterações'}
                    </Button>
                  )}
                </CardContent>

                <CardActions
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 1,
                  }}
                >
                  <Stack spacing={1} width="100%">
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.4,
                        borderRadius: 2,
                        borderColor: brandTheme.palette.borderSoft,
                        bgcolor: 'rgba(255,255,255,0.82)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Lista de Presentes
                      </Typography>
                      <Stack spacing={1}>
                        <Stack direction="row" gap={1}>
                          <Button
                            variant="outlined"
                            startIcon={<ContentCopy />}
                            onClick={() => !encerrada && handleCopyLink(shareUrl, lista.id)}
                            disabled={encerrada}
                            sx={{ flex: 1, p: 1 }}
                          >
                            Copiar link
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => {
                              trackEvent('open_list_public_link', {
                                source: 'admin_listas',
                                listId: lista.id,
                                slug: lista.slug,
                              });
                              window.open(listUrl, '_blank');
                            }}
                            disabled={encerrada}
                            sx={{ flex: 1, p: 1 }}
                          >
                            Abrir Lista
                          </Button>
                        </Stack>
                        {shareFeedback?.listId === lista.id && (
                          <Typography variant="caption" color="success.main">
                            {shareFeedback.message}
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          startIcon={<Share />}
                          onClick={() =>
                            !encerrada && handleShareLink(shareUrl, lista.id, lista.tituloLista)
                          }
                          disabled={encerrada}
                          fullWidth
                        >
                          Compartilhar lista
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<ListAlt />}
                          onClick={() => abrirGerenciarPresentes(lista)}
                          fullWidth
                          disabled={encerrada}
                        >
                          Gerenciar presentes
                        </Button>
                      </Stack>
                    </Paper>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.4,
                        borderRadius: 2,
                        borderColor: brandTheme.palette.borderSoft,
                        bgcolor: 'rgba(255,255,255,0.82)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Save the Date
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => abrirDialogSaveTheDate(lista)}
                        disabled={encerrada || savingList}
                      >
                        {saveDateAtivo ? 'Editar Save the Date' : 'Criar Save the Date'}
                      </Button>
                    </Paper>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.4,
                        borderRadius: 2,
                        borderColor: brandTheme.palette.borderSoft,
                        bgcolor: 'rgba(255,255,255,0.82)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Confirmação de presença
                      </Typography>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => abrirDialogPresenca(lista)}
                        disabled={encerrada || savingList}
                      >
                        {rsvpAtivo
                          ? 'Editar confirmação de presença'
                          : 'Criar confirmação de presença'}
                      </Button>

                      {isEditingCurrentList && rsvpAtivo && (
                        <Box sx={{ mt: 1.2 }}>
                          {loadingConfirmacoesPresenca ? (
                            <Box display="flex" justifyContent="center" py={1}>
                              <CircularProgress size={18} />
                            </Box>
                          ) : confirmacoesPresenca.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              Ainda não há confirmações nesta lista.
                            </Typography>
                          ) : (
                            <Stack spacing={0.7}>
                              <Typography variant="caption" color="text.secondary">
                                Confirmados ({confirmacoesPresenca.length})
                              </Typography>
                              {confirmacoesPresenca.slice(0, 6).map((item) => (
                                <Paper key={item.id} variant="outlined" sx={{ p: 0.8 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.nome}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(item.createdAt).toLocaleString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </Typography>
                                </Paper>
                              ))}
                              {confirmacoesPresenca.length > 6 && (
                                <Typography variant="caption" color="text.secondary">
                                  + {confirmacoesPresenca.length - 6} confirmações
                                </Typography>
                              )}
                            </Stack>
                          )}
                        </Box>
                      )}
                    </Paper>

                    {pixHistory.filter((k) => k.chave.trim() !== '').length >= 2 && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          void abrirDialogEditar(lista);
                          setShowPixHistoryDialog(true);
                        }}
                        fullWidth
                      >
                        Ver histórico de chaves
                      </Button>
                    )}
                  </Stack>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={fecharDialogCriar}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${brandTheme.palette.borderSoft}`,
            backgroundColor: 'rgba(255,255,255,0.97)',
          },
        }}
      >
        <DialogTitle>Criar nova lista</DialogTitle>
        <DialogContent dividers>
          {error && createDialogOpen && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} mt={1}>
            <TextField
              label="Título da lista"
              value={createForm.tituloLista}
              onChange={(event) => handleTituloChange(event.target.value)}
              onBlur={handleTituloBlur}
              placeholder="Ex: Chá de bebê da Lara"
              autoFocus
            />
            {publicBaseUrl && (
              <TextField
                label="Link privado"
                value={getPublicListUrl(privateSlugPreview || slugPrivadoPreview)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton
                      edge="end"
                      onClick={() =>
                        handleShareLink(getPublicListUrl(privateSlugPreview || slugPrivadoPreview))
                      }
                    >
                      <Share fontSize="small" />
                    </IconButton>
                  ),
                }}
                helperText={
                  copiado
                    ? 'Link pronto para compartilhar!'
                    : 'Toque para compartilhar no celular ou copiar o link no navegador.'
                }
              />
            )}
            <TextField
              label="Chave PIX (opcional)"
              value={createForm.pixKey}
              onChange={(event) => handlePixKeyChange(event.target.value)}
              onBlur={handlePixKeyBlur}
              placeholder="E-mail, telefone, CPF/CNPJ ou chave aleatória"
              error={createPixKeyInfo?.type === 'Invalida' && !createPixKeyInfo.isValid}
              helperText={
                createPixKeyInfo?.type === 'Invalida'
                  ? createPixKeyInfo.message
                  : createPixKeyInfo?.type === 'Telefone'
                    ? 'Telefone validado e ajustado para o formato PIX com +55.'
                    : 'Você pode informar agora ou depois. Para telefone, use +55DDNúmero (ex.: +551191234567).'
              }
            />
            {createPixKeyInfo && createPixKeyInfo.type !== 'Vazia' && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  color={createPixKeyInfo.isValid ? 'success' : 'error'}
                  label={
                    createPixKeyInfo.type === 'Aleatoria'
                      ? 'Chave aleatoria'
                      : `Chave ${createPixKeyInfo.type.toLowerCase()}`
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  {createPixKeyInfo.masked}
                </Typography>
              </Stack>
            )}
            {mostrarSeletorTemplate ? (
              <FormControl fullWidth>
                <InputLabel id="template-select-label">Modelo de lista</InputLabel>
                <Select
                  labelId="template-select-label"
                  value={createForm.templateSlug}
                  onChange={(event) => handleTemplateChange(event.target.value as string)}
                  label="Modelo de lista"
                  disabled={isLoadingTemplates}
                >
                  {orderedTemplates.map((template: ListaTemplateResumo) => (
                    <MenuItem key={template.slug} value={template.slug}>
                      {template.titulo}
                    </MenuItem>
                  ))}
                  <MenuItem value="">
                    <em>Sem modelo (personalizado)</em>
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {isLoadingTemplates
                    ? 'Carregando modelos disponíveis...'
                    : 'Escolha um modelo para começar com sugestões prontas para cada ocasião.'}
                </FormHelperText>
              </FormControl>
            ) : null}
            {templateSelecionado && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <CategoryOutlined fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {templateSelecionado.titulo}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {templateSelecionado.descricao}
                </Typography>
                {(templateSelecionado.destaques ?? []).length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                    {(templateSelecionado.destaques ?? []).map((destaque: string) => (
                      <Chip
                        key={destaque}
                        label={destaque}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                ) : null}
                {(templateSelecionado.presentes ?? []).length > 0 ? (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Este modelo sugere {(templateSelecionado.presentes ?? []).length} presentes para
                    você adicionar rapidamente.
                  </Typography>
                ) : null}
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogCriar} disabled={creating}>
            Cancelar
          </Button>
          <Button onClick={criarLista} variant="contained" disabled={creating}>
            {creating ? 'Salvando...' : 'Criar lista'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rsvpDialogOpen}
        onClose={fecharDialogPresenca}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${brandTheme.palette.borderSoft}`,
            backgroundColor: 'rgba(255,255,255,0.97)',
          },
        }}
      >
        <DialogTitle>Lista de Presença</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={rsvpForm.enabled}
                  onChange={(event) => handleRsvpToggle(event.target.checked)}
                />
              }
              label="Habilitar confirmação de presença"
            />
            <TextField
              label="Data do evento"
              type="date"
              value={rsvpForm.eventDate}
              onChange={(event) => handleRsvpDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Campo opcional para exibir na página de confirmação."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogPresenca} disabled={savingList}>
            Cancelar
          </Button>
          <Button onClick={salvarConfiguracaoPresenca} variant="contained" disabled={savingList}>
            {savingList ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveDateDialogOpen}
        onClose={fecharDialogSaveTheDate}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${brandTheme.palette.borderSoft}`,
            backgroundColor: 'rgba(255,255,255,0.97)',
          },
        }}
      >
        <DialogTitle>Save the Date</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={saveDateForm.enabled}
                  onChange={(event) => handleSaveDateToggle(event.target.checked)}
                />
              }
              label="Habilitar Save the Date"
            />
            <TextField
              label="Data do evento"
              type="date"
              value={saveDateForm.eventDate}
              onChange={(event) => handleSaveDateChange('eventDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Horário"
              type="time"
              value={saveDateForm.eventTime}
              onChange={(event) => handleSaveDateChange('eventTime', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Local"
              value={saveDateForm.location}
              onChange={(event) => handleSaveDateChange('location', event.target.value)}
              placeholder="Ex: Salão Espaço dos Sonhos"
            />
            <TextField
              label="Título (opcional)"
              value={saveDateForm.headline}
              onChange={(event) => handleSaveDateChange('headline', event.target.value)}
              placeholder="Ex: Reserve esta data especial"
            />
            <TextField
              label="Mensagem (opcional)"
              value={saveDateForm.description}
              onChange={(event) => handleSaveDateChange('description', event.target.value)}
              multiline
              minRows={3}
            />

            {selectedList?.id && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <SaveDateFotoUpload listaId={selectedList.id} isOpen={saveDateDialogOpen} />
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogSaveTheDate} disabled={savingList}>
            Cancelar
          </Button>
          <Button onClick={salvarConfiguracaoSaveTheDate} variant="contained" disabled={savingList}>
            {savingList ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPixHistoryDialog && pixHistory.length > 1}
        onClose={() => setShowPixHistoryDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${brandTheme.palette.borderSoft}`,
            backgroundColor: 'rgba(255,255,255,0.97)',
          },
        }}
      >
        <DialogTitle>Histórico PIX</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {loadingPixHistory ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : pixHistory.length === 0 ? (
              <Alert severity="info">Nenhuma chave cadastrada até o momento.</Alert>
            ) : (
              <Stack spacing={1}>
                {pixHistory.map((item) => {
                  const criadaEm = formatarDataHora(item.criadaEm);
                  const desativadaEm = formatarDataHora(item.desativadaEm);

                  return (
                    <Box
                      key={item.id}
                      p={2}
                      borderRadius={1}
                      border={
                        item.ativa
                          ? '1px solid rgba(46, 125, 50, 0.5)'
                          : '1px solid rgba(0,0,0,0.12)'
                      }
                      bgcolor={item.ativa ? 'success.light' : 'background.paper'}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {item.chave}
                        </Typography>
                        <Chip
                          size="small"
                          color={item.ativa ? 'success' : 'default'}
                          label={item.ativa ? 'Ativa' : 'Inativa'}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Criada em {criadaEm}
                        {desativadaEm ? ` • desativada em ${desativadaEm}` : ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPixHistoryDialog(false)} disabled={savingList}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Tooltip title="Nova lista" placement="left">
        <Fab
          color="primary"
          aria-label="Nova lista"
          onClick={() => {
            trackClick('create_list_fab');
            abrirDialogCriar();
          }}
          disabled={!podeCriarNovaLista}
          sx={{
            position: 'fixed',
            right: { xs: 76, sm: 88 },
            bottom: { xs: 18, sm: 24 },
            zIndex: 1299,
            opacity: showFloatingCreateButton && podeCriarNovaLista ? 1 : 0,
            transform: showFloatingCreateButton ? 'translateY(0)' : 'translateY(10px)',
            pointerEvents: showFloatingCreateButton && podeCriarNovaLista ? 'auto' : 'none',
            transition: 'opacity 180ms ease, transform 180ms ease',
            boxShadow: `0 18px 30px ${brandTheme.palette.heroGlow}`,
          }}
        >
          <Add />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default AdminListasPage;
